import { StylishNewSearchBar } from 'components/DesignSystems/New/StylishNewSearchBar';
import LegacyPratusTable, {
  LegacyExpandRowConfig,
} from 'components/DesignSystems/PratusTable/LegacyPratusTable';
import StylishNewTableAction from 'components/DesignSystems/New/StylishNewTableAction';
import { useState } from 'react';
import { useNavigate } from 'react-router';
import { SharedIcon } from 'components/SharedIcon/SharedIcon';
import {
  usePagedOrganizations,
  useUpdateOrganizationSubscription,
} from './subscriptionHooks';
import EditOrganizationSubscriptionModal from './EditOrganizationSubscriptionModal';
import { toast } from 'react-toastify';

interface SubscriptionDetails {
  saasSubscriptionStatus?: string;
  purchaser?: {
    emailId?: string;
    tenantId?: string;
  };
  quantity?: number;
}

interface Organization {
  group_guid: string;
  group_name: string;
  subscription_status?: string;
  subscription_details?: SubscriptionDetails;
  number_users?: number;
  contact_email?: string;
  number_licenses?: number;
}

interface SubscriptionLevel {
  label: string;
  value: string;
}

interface UpdatedOrganization {
  group_name: string;
  subscription_level: string;
  quantity: number;
  utility_impact?: string;
}

interface TableColumn {
  dataField: string;
  text: string;
  headerAttrs?: { title: string };
  attrs?: { title: string };
  formatter?: (cell: string, row: Organization) => React.ReactNode;
  headerStyle?: React.CSSProperties;
  headerAlign?: 'left' | 'center' | 'right';
  align?: 'left' | 'center' | 'right';
}

export const SubscriptionManagement: React.FC = () => {
  const navigate = useNavigate();
  const [currentPage, setCurrentPage] = useState<number>(1);
  const itemsPerPage = 20;
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [editingOrganization, setEditingOrganization] =
    useState<Organization | null>(null);

  const { data, isLoading, isError, error } = usePagedOrganizations(
    searchTerm,
    currentPage,
    itemsPerPage
  );

  const updateOrgSubscription = useUpdateOrganizationSubscription();

  const subscriptionLevels: SubscriptionLevel[] = [
    { label: 'Trial', value: 'Trial' },
    { label: 'Paid', value: 'Paid' },
    { label: 'Paid (Incident only)', value: 'Paid (Incident only)' },
    { label: 'Paid (Exercise only)', value: 'Paid (Exercise only)' },
    { label: 'Freemium', value: 'Freemium' },
  ];

  const utilityImpactOptions: SubscriptionLevel[] = [
    { label: 'None', value: 'None' },
    { label: 'DEMO', value: 'Demo' },
    { label: 'GMP', value: 'GMP' },
    { label: 'VELCO', value: 'VELCO' },
    { label: 'VEC', value: 'VEC' },
    { label: 'Vermont Utilities', value: 'Counties' },
  ];

  const columns: TableColumn[] = [
    {
      dataField: 'group_name',
      text: 'Organization Name',
      headerAttrs: { title: 'Sort By:' },
      attrs: { title: 'Organization Name' },
    },
    {
      dataField: 'subscription_status',
      text: 'Subscription Status',
      formatter: (cell: string, row: Organization) => {
        return <span>{row.subscription_status || 'N/A'}</span>;
      },
      attrs: { title: 'Subscription Status' },
    },
    {
      dataField: 'action',
      text: 'Actions',
      formatter: (cell: string, row: Organization) => (
        <StylishNewTableAction
          name="Subscription"
          row={row}
          onEdit={() => handleEditOrg(row)}
          onDelete={undefined}
          onView={undefined}
          onAddOn={undefined}
        />
      ),
      headerStyle: { width: '100px' },
      attrs: { title: 'Actions' },
    },
  ];

  const expandRow: LegacyExpandRowConfig<Organization> = {
    parentClassName: 'parent-expand-tr',
    className: 'child-expand-tr',
    renderer: (row: Organization) => {
      const details = row.subscription_details || {};
      return (
        <div className="row">
          <div className="col-md-6 mb-2">
            <h6 className="mb-1">Number of Members</h6>
            {row.number_users || 'N/A'}
          </div>
          <div className="col-md-6 mb-2">
            <h6 className="mb-1">Organization Creator Email</h6>
            {row.contact_email || details.purchaser?.emailId || 'N/A'}
          </div>
          <div className="col-md-6 mb-2">
            <h6 className="mb-1">Number of Licenses (Quantity)</h6>
            {row.number_licenses || 'N/A'}
          </div>
          <div className="col-md-6 mb-2">
            <h6 className="mb-1">Associated Tenant ID</h6>
            {details.purchaser?.tenantId || 'N/A'}
          </div>
        </div>
      );
    },
    showExpandColumn: true,
  };

  const onChangeSearchTerm = (value: string): void => {
    setCurrentPage(1);
    setSearchTerm(value);
  };

  const handlePageClick = (page: number): void => {
    setCurrentPage(page);
  };

  const handleEditOrg = (org: Organization): void => {
    setEditingOrganization(org);
  };

  const handleSaveOrg = async (updated: UpdatedOrganization): Promise<void> => {
    try {
      if (!editingOrganization?.group_guid) {
        throw new Error('No organization selected');
      }

      await updateOrgSubscription.mutateAsync({
        group_name: updated.group_name,
        group_guid: editingOrganization.group_guid,
        subscription_level: updated.subscription_level,
        utility_impact: updated.utility_impact,
        quantity: updated.quantity,
      });
      toast.success('Organization subscription updated successfully');
      setEditingOrganization(null);
    } catch (error) {
      console.error('Failed to update organization subscription:', error);
      toast.error('Failed to update organization subscription');
    }
  };

  return (
    <>
      <div className="site-main">
        <div className="site-main__profile">
          <div className="container-fluid">
            <div className="row">
              <div className="col-md-12">
                <nav aria-label="breadcrumb">
                  <ol className="breadcrumb">
                    <li className="breadcrumb-item">
                      <button onClick={() => navigate(-1)}>
                        <SharedIcon
                          iconName="keyboard_arrow_left"
                          classes="me-2"
                        />
                        Back
                      </button>
                    </li>
                    <li className="breadcrumb-item active" aria-current="page">
                      Subscription Management
                    </li>
                  </ol>
                </nav>
                <div className="d-flex mb-4">
                  <StylishNewSearchBar
                    value={searchTerm}
                    onChangeSearchTerm={onChangeSearchTerm}
                    placeholder={'Search by organization..'}
                  />
                </div>
                <div className="form-block">
                  <p className="weight-600">
                    {data?.total || 0} Organization(s)
                  </p>
                  <LegacyPratusTable
                    keyField={'group_guid'}
                    columns={columns}
                    rows={data?.organizations || []}
                    pagination={true}
                    page={currentPage}
                    perPage={itemsPerPage}
                    total={data?.total}
                    noDataIndication={
                      isLoading ? (
                        'Loading Organizations...'
                      ) : isError ? (
                        <div className="form-text form-error">
                          {error?.message}
                        </div>
                      ) : (
                        'No Organizations'
                      )
                    }
                    onPageChange={handlePageClick}
                    expandRow={expandRow}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <EditOrganizationSubscriptionModal
        show={!!editingOrganization}
        onClose={() => setEditingOrganization(null)}
        orgData={editingOrganization}
        onSave={handleSaveOrg}
        subscriptionLevels={subscriptionLevels}
        utilityImpactOptions={utilityImpactOptions}
      />
    </>
  );
};
